import type { LDLogger } from 'launchdarkly-js-client-sdk';
import type { AsyncProviderConfig } from 'launchdarkly-react-client-sdk';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';

import { loggerUtils } from '@/Logging';

import { getLaunchDarklyUserKeyCookieValue } from '../cookies';

const logToConsole = (message: string): void =>
  loggerUtils.logMessageToConsole(`[Launch Darkly] ${message}`);

const ldLogger: LDLogger = {
  debug: () => null, // Don't log debug messages in staging/prod
  info: logToConsole,
  warn: logToConsole,
  error: (message) => {
    if (message && message.includes('network error')) {
      // These are not actionable and logging them as errors pollutes Sentry
      logToConsole(message);
    } else {
      // If there's a different LD problem, we want to know about it, so log it as an error which gets captured by Sentry
      loggerUtils.logException(new Error(`[Launch Darkly] ${message}`));
    }
  },
};

/**
 * RealLaunchDarklyProviderHoc is a higher order component, that asynchronously
 * sets up the LaunchDarkly client and retrieves the flags before. Once
 * resolved, this returns a Provider component.
 */
export async function RealLaunchDarklyProviderHoc(clientId: string): Promise<React.ElementType> {
  const userKey = getLaunchDarklyUserKeyCookieValue();
  const user = userKey ? { key: userKey } : { anonymous: true };
  const config: AsyncProviderConfig = {
    user,
    clientSideID: clientId,
    reactOptions: {
      useCamelCaseFlagKeys: false,
    },
    options: {
      logger: ldLogger,
    },
  };
  return asyncWithLDProvider(config);
}
